import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

import { CALLOUT_EMPHASIS, CALLOUT_SIZE, CALLOUT_TONE } from './types/callout-variants-enums';

const calloutVariants = cva('relative flex w-full items-start rounded-surface border', {
  variants: {
    tone: {
      [CALLOUT_TONE.NEUTRAL]: '',
      [CALLOUT_TONE.INFO]: '',
      [CALLOUT_TONE.SUCCESS]: '',
      [CALLOUT_TONE.WARNING]: '',
      [CALLOUT_TONE.DANGER]: '',
    },
    emphasis: {
      [CALLOUT_EMPHASIS.SUBTLE]:
        '[&_[data-slot=callout-description]]:text-content-secondary',
      [CALLOUT_EMPHASIS.STRONG]:
        'border-transparent [&_[data-slot=callout-description]]:text-current',
    },
    size: {
      [CALLOUT_SIZE.COMPACT]:
        'gap-2 p-3 [&_[data-slot=callout-icon]]:size-4 [&_[data-slot=callout-icon]>svg]:size-4',
      [CALLOUT_SIZE.DEFAULT]:
        'gap-3 p-4 [&_[data-slot=callout-icon]]:size-[18px] [&_[data-slot=callout-icon]>svg]:size-[18px]',
    },
  },
  compoundVariants: [
    {
      tone: CALLOUT_TONE.NEUTRAL,
      emphasis: CALLOUT_EMPHASIS.SUBTLE,
      class:
        'border-status-neutral-border bg-status-neutral-surface text-status-neutral-foreground',
    },
    {
      tone: CALLOUT_TONE.INFO,
      emphasis: CALLOUT_EMPHASIS.SUBTLE,
      class: 'border-status-info-border bg-status-info-surface text-status-info-foreground',
    },
    {
      tone: CALLOUT_TONE.SUCCESS,
      emphasis: CALLOUT_EMPHASIS.SUBTLE,
      class:
        'border-status-success-border bg-status-success-surface text-status-success-foreground',
    },
    {
      tone: CALLOUT_TONE.WARNING,
      emphasis: CALLOUT_EMPHASIS.SUBTLE,
      class:
        'border-status-warning-border bg-status-warning-surface text-status-warning-foreground',
    },
    {
      tone: CALLOUT_TONE.DANGER,
      emphasis: CALLOUT_EMPHASIS.SUBTLE,
      class: 'border-status-danger-border bg-status-danger-surface text-status-danger-foreground',
    },
    {
      tone: CALLOUT_TONE.NEUTRAL,
      emphasis: CALLOUT_EMPHASIS.STRONG,
      class: 'bg-status-neutral-solid text-status-neutral-solid-foreground',
    },
    {
      tone: CALLOUT_TONE.INFO,
      emphasis: CALLOUT_EMPHASIS.STRONG,
      class: 'bg-status-info-solid text-status-info-solid-foreground',
    },
    {
      tone: CALLOUT_TONE.SUCCESS,
      emphasis: CALLOUT_EMPHASIS.STRONG,
      class: 'bg-status-success-solid text-status-success-solid-foreground',
    },
    {
      tone: CALLOUT_TONE.WARNING,
      emphasis: CALLOUT_EMPHASIS.STRONG,
      class: 'bg-status-warning-solid text-status-warning-solid-foreground',
    },
    {
      tone: CALLOUT_TONE.DANGER,
      emphasis: CALLOUT_EMPHASIS.STRONG,
      class: 'bg-status-danger-solid text-status-danger-solid-foreground',
    },
  ],
  defaultVariants: {
    tone: CALLOUT_TONE.NEUTRAL,
    emphasis: CALLOUT_EMPHASIS.SUBTLE,
    size: CALLOUT_SIZE.DEFAULT,
  },
});

export interface CalloutRootProps
  extends ComponentProps<'div'>,
    VariantProps<typeof calloutVariants> {
  asChild?: boolean;
}

export function CalloutRoot({
  asChild = false,
  className,
  tone,
  emphasis,
  size,
  ...props
}: CalloutRootProps) {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="callout"
      className={cn(calloutVariants({ tone, emphasis, size }), className)}
      {...props}
    />
  );
}

type CalloutIconProps = Omit<ComponentProps<'span'>, 'aria-hidden'>;

export function CalloutIcon({ className, ...props }: CalloutIconProps) {
  return (
    <span
      aria-hidden="true"
      data-slot="callout-icon"
      className={cn('mt-0.5 flex shrink-0 items-center justify-center self-start', className)}
      {...props}
    />
  );
}

export function CalloutContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="callout-content"
      className={cn('flex min-w-0 flex-1 flex-col gap-1', className)}
      {...props}
    />
  );
}

export interface CalloutTitleProps extends ComponentProps<'h3'> {
  asChild?: boolean;
}

export function CalloutTitle({
  asChild = false,
  className,
  ...props
}: CalloutTitleProps) {
  const Comp = asChild ? Slot : 'h3';

  return (
    <Comp
      data-slot="callout-title"
      className={cn('text-sm/5 font-semibold text-current', className)}
      {...props}
    />
  );
}

export function CalloutDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="callout-description"
      className={cn('text-sm/5 font-normal', className)}
      {...props}
    />
  );
}

export function CalloutActions({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="callout-actions"
      className={cn(
        'flex flex-wrap items-center gap-2 pt-2 max-sm:flex-col max-sm:items-stretch max-sm:[&>*]:w-full',
        className
      )}
      {...props}
    />
  );
}
